import { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Search as SearchIcon, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import {
  useUserConfig,
  useUpdateUserConfig,
  useDeviceConfig,
  DEVICE_CONFIG_DEFAULTS,
} from '../api/config';
import { useVoiceSearch } from '../hooks/useVoiceSearch';
import { useToast } from './ToastHost';

interface Provider {
  slug: string;
  name: string;
  searchUrl: string;
  isDefault: boolean;
}

interface AcItem {
  slug: string;
  title: string;
  url: string;
  groupSlug: string;
  setSlug: string;
}

export function SearchBar(): React.ReactElement {
  const [q, setQ] = useState('');
  const [focus, setFocus] = useState(false);
  const [items, setItems] = useState<AcItem[]>([]);
  // active < 0 means "no autocomplete item highlighted" - Enter falls through
  // to the form's external-search submit. Press ↓ once to start navigating.
  const [active, setActive] = useState(-1);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const { data: cfg } = useUserConfig();
  const { data: dc } = useDeviceConfig();
  const updateCfg = useUpdateUserConfig();
  const navigate = useNavigate();
  const { push } = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const debRef = useRef<number | null>(null);
  const pickerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    api
      .get('search/providers')
      .json<Provider[]>()
      .then((list) => setProviders(list.filter((p) => p.slug !== 'internal')))
      .catch(() => setProviders([]));
  }, []);

  // Close the provider picker on outside click.
  useEffect(() => {
    if (!pickerOpen) return;
    const onDoc = (e: MouseEvent): void => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [pickerOpen]);

  const currentProvider =
    providers.find((p) => p.slug === (cfg?.searchProviderSlug ?? 'duckduckgo')) ??
    providers.find((p) => p.isDefault) ??
    providers[0] ??
    null;

  const internalAcEnabled = cfg?.internalAutocompleteEnabled ?? true;

  // Fire autocomplete fetches as the user types. Skipped entirely when
  // internalAutocompleteEnabled is false so the user really sees nothing.
  useEffect(() => {
    if (debRef.current) window.clearTimeout(debRef.current);
    if (!internalAcEnabled || q.trim().length < 2) {
      setItems([]);
      return;
    }
    debRef.current = window.setTimeout(async () => {
      try {
        const res = await api
          .get(`search/autocomplete?q=${encodeURIComponent(q.trim())}&limit=8`)
          .json<AcItem[]>();
        setItems(res);
        setActive(-1); // no item pre-selected; user must press ↓ to pick
      } catch {
        setItems([]);
      }
    }, 180);
    return () => {
      if (debRef.current) window.clearTimeout(debRef.current);
    };
  }, [q, internalAcEnabled]);

  // Open a link, honoring the user's linkTarget setting.
  function openLink(url: string): void {
    const target =
      cfg?.linkTarget === 'SELF' ? '_self' : cfg?.linkTarget === 'PARENT' ? '_parent' : '_blank';
    window.open(url, target, 'noopener');
    setQ('');
    setItems([]);
  }

  // Pick the first internal autocomplete match and open it. Used by both the
  // "INTERNAL" Auto-Search-After-Voice mode and any future internal-only
  // shortcut.
  function openTopInternal(): boolean {
    if (items.length === 0) return false;
    openLink(items[0]!.url);
    return true;
  }

  function doExternalSearch(): void {
    const text = q.trim();
    if (!text) return;
    const url = (currentProvider?.searchUrl ?? 'https://duckduckgo.com/?q={query}').replace(
      '{query}',
      encodeURIComponent(text),
    );
    openLink(url);
  }

  const voice = useVoiceSearch({
    silenceDelayMs: dc?.voiceSilenceDelayMs ?? DEVICE_CONFIG_DEFAULTS.voiceSilenceDelayMs,
    initialTimeoutMs: 10_000,
    onInterim: (text) => setQ(text),
    onFinal: (text) => setQ((v) => (v ? v + ' ' + text : text)),
    onError: (m) => push({ message: m, severity: 'error' }),
    onStopped: (bySilence) => {
      if (!bySilence) return;
      const mode = cfg?.autoSearchAfterVoice ?? 'OFF';
      if (mode === 'OFF') return;
      if (mode === 'INTERNAL') {
        // Wait one debounce cycle so the autocomplete has had a chance to land.
        window.setTimeout(() => {
          if (!openTopInternal()) {
            push({ message: 'No matching links', severity: 'info' });
          }
        }, 250);
        return;
      }
      if (mode === 'PROVIDER') {
        doExternalSearch();
      }
    },
  });

  function selectProvider(slug: string): void {
    setPickerOpen(false);
    // Write through the proper TanStack mutation so the local user-config
    // cache updates the instant the server responds - without it the next
    // search would still use the *old* provider until a manual refresh.
    updateCfg.mutate({ searchProviderSlug: slug }, {
      onError: () => push({ message: 'Could not update search provider', severity: 'error' }),
    });
  }

  function handleKey(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      // Allow going back to -1 ("no selection") so Enter resumes searching.
      setActive((a) => Math.max(a - 1, -1));
    } else if (e.key === 'Escape') {
      setItems([]);
      setActive(-1);
    }
    // Enter is decided in the form's onSubmit handler - keeping a single
    // entry point eliminates the double-fire we were getting where both
    // openLink() and doExternalSearch() ran on the same keystroke.
  }

  const showAc = focus && items.length > 0;

  return (
    <form
      className="sl-search-form"
      onSubmit={(e) => {
        e.preventDefault();
        // If the user has explicitly highlighted an autocomplete item with ↓,
        // open that link. Otherwise run the external provider search. Single
        // entry point so we never double-fire.
        if (active >= 0 && active < items.length) {
          openLink(items[active]!.url);
          return;
        }
        doExternalSearch();
      }}
    >
      <input
        ref={inputRef}
        className="sl-search-input"
        placeholder={voice.recording ? 'Listening…' : `Search ${currentProvider?.name ?? 'the web'}`}
        autoComplete="off"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => setFocus(true)}
        onBlur={() => window.setTimeout(() => setFocus(false), 120)}
        onKeyDown={handleKey}
      />

      {/* Mic - speech-to-text */}
      <button
        type="button"
        className={`sl-mic-btn${voice.recording ? ' sl-recording' : ''}`}
        aria-label={voice.recording ? 'Stop voice search' : 'Voice search'}
        title={voice.recording ? 'Stop voice search' : 'Voice search'}
        onClick={voice.toggle}
        disabled={!voice.supported}
      >
        {voice.recording ? <MicOff size={14} /> : <Mic size={14} />}
      </button>

      {/* Search button - fires the external provider, same as Enter */}
      <button
        type="button"
        className="sl-search-go-btn"
        aria-label={`Search with ${currentProvider?.name ?? 'provider'}`}
        title={`Search with ${currentProvider?.name ?? 'provider'}`}
        onClick={() => doExternalSearch()}
      >
        <SearchIcon size={14} />
      </button>

      {/* Provider picker - clicking the provider's favicon opens the picker */}
      <div className="sl-provider-picker-wrap" ref={pickerRef}>
        <button
          type="button"
          className="sl-provider-picker-btn"
          aria-label={`Search provider: ${currentProvider?.name ?? 'choose'} (click to change)`}
          title={`Search provider: ${currentProvider?.name ?? 'choose'} (click to change)`}
          onClick={() => setPickerOpen((v) => !v)}
        >
          {currentProvider?.searchUrl ? (
            <img
              src={`/api/icon-lookup-img?url=${encodeURIComponent(originOfSearchUrl(currentProvider.searchUrl))}`}
              alt=""
              className="sl-provider-picker-favicon"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src = '/default-icon.png';
              }}
            />
          ) : (
            <ChevronDown size={14} />
          )}
        </button>
        {pickerOpen && (
          <div className="sl-provider-picker" role="listbox">
            {providers.map((p) => {
              const isActive = p.slug === currentProvider?.slug;
              return (
                <button
                  key={p.slug}
                  type="button"
                  className={`sl-provider-picker-item${isActive ? ' sl-active' : ''}`}
                  onClick={() => selectProvider(p.slug)}
                >
                  <img
                    src={`/api/icon-lookup-img?url=${encodeURIComponent(originOfSearchUrl(p.searchUrl))}`}
                    alt=""
                    className="sl-provider-picker-icon"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).src = '/default-icon.png';
                    }}
                  />
                  <span>{p.name}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {showAc && (
        <div className="sl-search-ac" role="listbox">
          {items.map((it, i) => (
            <div
              key={it.slug}
              className="sl-search-ac-item"
              data-active={i === active}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => {
                e.preventDefault();
                openLink(it.url);
              }}
            >
              <img
                className="sl-search-ac-icon"
                src={`/api/icon-lookup-img?url=${encodeURIComponent(it.url)}`}
                alt=""
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src = '/default-icon.png';
                }}
              />
              <div className="sl-search-ac-main">
                <div className="sl-search-ac-title">{it.title || it.url}</div>
                <div className="sl-search-ac-meta">{safeHost(it.url)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </form>
  );
}

function safeHost(u: string): string {
  try {
    return new URL(u).hostname.replace(/^www\./, '');
  } catch {
    return u;
  }
}

// SearchProvider.searchUrl looks like "https://duckduckgo.com/?q={query}".
// The icon service expects an origin to look up the favicon.
function originOfSearchUrl(searchUrl: string): string {
  try {
    return new URL(searchUrl.replace('{query}', 'x')).origin;
  } catch {
    return searchUrl;
  }
}
